import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import AppStoreLink from '../components/common/AppStoreLink';
import { cancelTripSearch } from '../services/api/bookings';
import { useTripTracking } from '../hooks/useTripTracking';
import { useBookingDraft } from '../features/booking/BookingDraft';

/**
 * Waiting for a driver.
 *
 * The app's `searching-driver` screen, reduced to what the web needs. The wait
 * is bounded and visible: a customer who has just authorised a card hold should
 * never be left watching an indefinite spinner, so the search either matches, or
 * ends in a state that says so and offers two ways on.
 *
 * Tracking begins the moment a driver accepts, which is why this reuses
 * `useTripTracking` rather than polling separately — the socket that will carry
 * the live trip is the same one that reports the acceptance.
 */

/** Matches the app's bounded search window. */
const SEARCH_TIMEOUT_MS = 90_000;

type Outcome = 'searching' | 'timeout' | 'cancelled';

const BookingSearchingPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { reset } = useBookingDraft();
  const tripId = params.get('tripId') ?? undefined;

  const { trip, connected } = useTripTracking(tripId);
  const [outcome, setOutcome] = useState<Outcome>('searching');

  const accepted = Boolean(trip?.driverId) || trip?.status === 'ACCEPTED';

  useEffect(() => {
    if (!accepted || !tripId) return;

    /* The draft has become a real trip; clear it so a later booking starts with
       a fresh idempotency key rather than resuming this one. */
    reset();
    navigate(`/account/trips/${encodeURIComponent(tripId)}`, { replace: true });
  }, [accepted, navigate, reset, tripId]);

  useEffect(() => {
    if (outcome !== 'searching') return undefined;

    const timer = window.setTimeout(() => setOutcome('timeout'), SEARCH_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [outcome]);

  const handleCancel = useCallback(async () => {
    if (tripId) {
      try {
        await cancelTripSearch(tripId);
      } catch {
        /* The search may already have ended; the customer still leaves. */
      }
    }

    setOutcome('cancelled');
    reset();
  }, [reset, tripId]);

  if (!tripId) {
    return (
      <Shell heading="No trip to track">
        <p className="sw-estimate-note">This link is missing a trip reference.</p>
        <Link className="sw-btn-primary" to="/account">
          Your deliveries
        </Link>
      </Shell>
    );
  }

  if (outcome === 'cancelled') {
    return (
      <Shell heading="Search cancelled">
        <p className="sw-estimate-note">
          No driver was assigned and no fare was charged. Any hold your bank placed is released.
        </p>
        <div className="sw-hero-actions">
          <Link className="sw-btn-primary" to="/#estimate">
            Price another route
          </Link>
          <Link className="sw-link-action" to="/account">
            Your deliveries &rarr;
          </Link>
        </div>
      </Shell>
    );
  }

  if (outcome === 'timeout') {
    return (
      <Shell heading="No driver yet">
        <p className="sw-estimate-note">
          We could not match a driver for this route right now. Nothing has been charged. You can
          keep waiting in the app, which will notify you, or try again later.
        </p>
        <div className="sw-hero-actions">
          <AppStoreLink placement="search_timeout">Keep waiting in the app</AppStoreLink>
          <button className="sw-link-action" onClick={() => void handleCancel()} type="button">
            Cancel this search
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell heading="Finding your driver">
      <p className="sw-estimate-note">
        We are matching you with a nearby driver. This usually takes under a minute — keep this page
        open and it will move to live tracking automatically.
      </p>

      <p className="sw-estimate-note">
        {connected ? 'Connected — waiting for a match.' : 'Reconnecting…'}
      </p>

      <button className="sw-btn-secondary" onClick={() => void handleCancel()} type="button">
        Cancel search
      </button>
    </Shell>
  );
};

const Shell: React.FC<{ heading: string; children: React.ReactNode }> = ({ heading, children }) => (
  <div className="landing-shell">
    <Header />
    <main>
      <section className="sw-section">
        <div className="container">
          <div className="sw-section-head">
            <span className="sw-eyebrow">Dispatch</span>
            <h1>{heading}</h1>
          </div>
          {children}
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default BookingSearchingPage;
